import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ArrowsClockwise, Database } from "@phosphor-icons/react";

export default function DbStatsTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastFetched, setLastFetched] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/db-stats");
      setData(r.data);
      setLastFetched(new Date());
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load DB stats");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/admin/db-stats");
        if (!cancelled) { setData(r.data); setLastFetched(new Date()); }
      } catch (e) {
        if (!cancelled) toast.error(e?.response?.data?.detail || "Failed to load DB stats");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading && !data) {
    return <div className="py-16 text-center text-sm text-gray-400">Loading database stats…</div>;
  }
  if (!data) return null;

  const t = data.totals;
  return (
    <div className="space-y-5" data-testid="dbstats-tab">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <Database size={18} className="text-violet-600" />
            Database snapshot
          </h2>
          <p className="text-xs text-gray-500 mt-0.5">
            Live read of MongoDB metadata only — no documents are scanned.
            {lastFetched ? <> · Last refreshed: <span className="tabular-nums">{lastFetched.toLocaleTimeString("en-IN")}</span></> : null}
          </p>
        </div>
        <Button onClick={load} disabled={loading} variant="outline" data-testid="dbstats-refresh">
          <ArrowsClockwise size={14} className="mr-1.5" />
          Refresh
        </Button>
      </div>

      {/* Roll-up cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Total documents" value={t.documents.toLocaleString("en-IN")} />
        <Stat label="Document storage" value={t.storage_human} />
        <Stat label="Index storage" value={t.index_human} />
        <Stat label="Combined size" value={t.grand_total_human} emphasis />
      </div>

      {/* Per-collection table */}
      <div className="border border-gray-200 rounded-md overflow-hidden bg-white">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-[11px] uppercase tracking-wider text-gray-500">
            <tr>
              <th className="text-left px-3 py-2 font-semibold">Collection</th>
              <th className="text-right px-3 py-2 font-semibold">Documents</th>
              <th className="text-right px-3 py-2 font-semibold">Avg doc</th>
              <th className="text-right px-3 py-2 font-semibold">Data</th>
              <th className="text-right px-3 py-2 font-semibold">Indexes</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {data.collections.map((c) => (
              <tr key={c.name} data-testid={`dbstats-row-${c.name}`} className="hover:bg-gray-50">
                <td className="px-3 py-2 font-mono text-[12px] text-gray-700">{c.name}</td>
                <td className="px-3 py-2 text-right tabular-nums">{c.documents.toLocaleString("en-IN")}</td>
                <td className="px-3 py-2 text-right tabular-nums text-gray-500">{c.avg_doc_bytes ? `${c.avg_doc_bytes} B` : "—"}</td>
                <td className="px-3 py-2 text-right tabular-nums">{c.storage_human}</td>
                <td className="px-3 py-2 text-right tabular-nums text-gray-500">{c.index_human}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {data.database?.storage_bytes ? (
        <div className="text-[11px] text-gray-400">
          Whole-database storage size (incl. system collections):
          <span className="ml-1 tabular-nums">{data.database.storage_human}</span>
        </div>
      ) : null}

      <div className="rounded-md border border-violet-100 bg-violet-50 px-4 py-3 text-xs text-violet-800">
        <b>What this is:</b> Sizes here are <i>live</i> from MongoDB&apos;s metadata (not scanned).
        For Pulse&apos;s 10-user workload, expect the user-generated collections (call_logs, firm_notes, tasks, reminders)
        to grow by roughly 100-300 KB per month — comfortable for years.
      </div>
    </div>
  );
}

function Stat({ label, value, emphasis }) {
  return (
    <div className={`rounded-md border ${emphasis ? "border-violet-200 bg-violet-50" : "border-gray-200 bg-white"} px-4 py-3`}>
      <div className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold">{label}</div>
      <div className={`mt-1 text-xl font-semibold tabular-nums ${emphasis ? "text-violet-900" : "text-gray-900"}`}>
        {value}
      </div>
    </div>
  );
}
